<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewPaymentVoucher', params: { id: id } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Payment Voucher
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">PV Generation Approval</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <form>
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Approval Form</h1>
              </div>

              <div class="form-body position-relative">
                <div v-if="loadingDetails" class="loading-overlay text-center">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading approval details...</p>
                </div>
                <!-- Payment Item -->
                <div class="row mb-3 payment-item-wrapper justify-content-center">
                  <div class="row content-wrapper section-border h-100 w-100">
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label align-items-center">Beneficiary</label>
                        <p type="text" class="form-control view-only-input text-center" readonly>
                          {{ form.beneficiary || 'N/A' }}
                        </p>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Initiated Date</label>
                        <p type="text" class="form-control view-only-input text-center" readonly>
                          {{ formatInitDate(form.pvInitDate) || 'N/A' }}
                        </p>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Bank</label>
                        <p type="text" class="form-control view-only-input text-center" readonly>
                          {{ form.bank || 'N/A' }}
                        </p>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Gross Amount</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + form.grossAmount || 0" readonly>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Total Deductions</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + form.deductions || 0" readonly>
                      </div>
                    </div>
                    <div class="col no-border d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Net Amount</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + form.netAmount || 0" readonly>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row mb-3">
                  <!-- Particulars -->
                  <div class="col-md-12 form-group">
                    <h5 class="mda-title mb-0 text-center fw-semibold">Particulars: <span class="particulars">{{
                      form.particulars ||
                      'N/A' }}</span></h5>
                  </div>
                </div>

                <!-- Deductions -->
                <div class="row mb-3 payment-item-wrapper justify-content-center">
                  <div class="table-responsive">
                    <h5 class="mda-title ms-1 mb-2 text-start">Deductions</h5>

                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-light">
                        <tr>
                          <th>Deduction</th>
                          <th>Description</th>
                          <!-- <th>Percentage</th> -->
                          <th>Amount (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="deductions.length === 0">
                          <td colspan="4" class="text-muted">No deductions added</td>
                        </tr>
                        <tr v-for="(item, index) in deductions" :key="index"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td>
                            {{ item.deduction }}
                          </td>
                          <td>
                            {{ item.description }}
                          </td>
                          <!-- <td>
                            {{ item.percentage > 0 ? item.percentage + '%' : '-' }}
                          </td> -->
                          <td>
                            {{ Number(item.amountReleased || 0).toLocaleString() }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Economic Line Items -->
                  <div class="table-responsive mt-4">
                    <h5 class="mda-title ms-1 mb-2 text-start">Economic Line Items</h5>
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-light">
                        <tr>
                          <th>Eco Code</th>
                          <th>Title</th>
                          <th>Amount Budgeted (₦)</th>
                          <th>Amount Released (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="economicItems.length === 0">
                          <td colspan="4" class="text-muted">No economic line items added</td>
                        </tr>
                        <tr v-for="(item, index) in economicItems" :key="index"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td>
                            {{ item.ecoCode }}
                          </td>
                          <td>
                            {{ item.title }}
                          </td>
                          <td>
                            {{ Number(item.amountBudgeted || 0).toLocaleString() }}
                          </td>
                          <td>
                            {{ Number(item.amountReleased || 0).toLocaleString() }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="row mb-3 align-items-center justify-content-center">
                  <!-- First Level Approval -->
                  <div v-if="firstLevelBoxVisible" :class="['row form-group position-relative floating-label-group justify-content-center',
                    firstLevelSaved ? 'col-md-12' : 'col-md-12 content-wrapper section-border']">
                    <div :class="['label-left d-flex justify-content-end align-items-center',
                      firstLevelSaved ? 'col-md-3' : 'col-md-2']">
                      <label for="year" class="form-label">{{ firstLevelLabel }}</label>
                    </div>
                    <div :class="['input-right',
                      firstLevelSaved ? 'col-md-9' : 'col-md-10']">
                      <div class="col-md-12 input-right">
                        <textarea v-if="canEditFirstLevelRemark" class="form-control" id="description"
                          v-model="form.dopRemarks" rows="3" required></textarea>

                        <input v-else type="text" class="form-control view-only-input" v-model="form.dopRemarks"
                          readonly />

                      </div>
                    </div>
                  </div>

                  <!-- Second Level Approval -->
                  <!-- <div v-if="showSecondLevelReturnContext" class="col-md-12 mb-3">
                    <div class="alert alert-warning mb-0">
                      <p class="mb-1 fw-semibold">Returned for correction</p>
                      <p class="mb-1"><strong>{{ secondLevelReturnContext.label }}:</strong></p>
                      <p class="mb-0">{{ secondLevelReturnContext.value }}</p>
                    </div>
                  </div> -->

                  <div v-if="secondLevelBoxVisible" :class="['row form-group position-relative floating-label-group justify-content-center',
                    secondLevelSaved ? 'col-md-12' : 'col-md-12 content-wrapper section-border']">
                    <div :class="['label-left d-flex justify-content-end align-items-center',
                      secondLevelSaved ? 'col-md-3' : 'col-md-2']">
                      <label for="year" class="form-label">{{ secondLevelLabel }}</label>
                    </div>
                    <div :class="['input-right',
                      secondLevelSaved ? 'col-md-9' : 'col-md-10']">
                      <div class="col-md-12 input-right">
                        <textarea v-if="canEditSecondLevelRemark" class="form-control" id="description"
                          v-model="form.dotRemarks" rows="3" required style="font-weight: 600;"></textarea>

                        <input v-else type="text" class="form-control view-only-input" v-model="form.dotRemarks"
                          readonly />

                      </div>
                    </div>
                  </div>

                  <!-- Hidden placeholder keeps layout stable when user has no visibility -->
                  <!-- <div v-if="!firstLevelBoxVisible && !secondLevelBoxVisible" class="col-md-12 text-center text-muted">
                    No approval remark is available for your role at this stage.
                  </div> -->

                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group mt-3">
                  <div class="col-md-2 label-left d-flex justify-content-end align-items-center"><label for="pvStatus"
                      class="form-label">Status</label></div>
                  <div class="col-md-10 input-right">
                    <input id="pvStatus" type="text" class="form-control view-only-input" :value="pvStatusLabel"
                      readonly>
                  </div>
                </div>
              </div>

              <!-- Submit Button -->
              <div v-if="!loadingDetails" class="d-flex justify-content-end mt-4">
                <div class="next-btn">
                  <button v-if="canShowApproveButton" type="button" class="btn btn-success accept-btn me-2"
                    @click="handleApprove" :disabled="loadingAccept || submitted">
                    <span v-if="loadingAccept" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ loadingAccept ? 'Processing...' : 'Approve' }}
                  </button>

                  <button v-if="canShowReturnButton" type="button" class="btn btn-warning return-btn  me-2"
                    @click="handleReturn" :disabled="loadingReturn || submitted">
                    <span v-if="loadingReturn" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ loadingReturn ? 'Processing...' : 'Return' }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/user';


const root_api_url = process.env.VUE_APP_API_ROOT_URL
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

const userStore = useUserStore()

const route = useRoute()
const id = route.params.id || route.query.id
const loadingAccept = ref(false)
const loadingReturn = ref(false)
const submitted = ref(false)
const loadingDetails = ref(false)
const pvPayload = ref(null)


// Reactive form data
const form = reactive({
  beneficiary: '',
  pvInitDate: '',
  paymentDate: '',
  bank: '',
  grossAmount: '',
  deductions: '',
  netAmount: '',
  dopRemarks: '',
  dotRemarks: '',
})

const deductions = ref([])
const economicItems = ref([])

const hasRole = (role) => !!(userStore.profile?.role || {})[role]

const isFirstLevelApprovedByBackend = (pv = {}) => {
  return pv.is1stLvlApproved === true || pv['1stLvlApproved'] === true
}

const isSecondLevelApprovedByBackend = (pv = {}) => {
  return pv.is2ndLvlApproved === true || pv['2ndLvlApproved'] === true
}

const isReturnPVToInitiatorByBackend = (pv = {}) => {
  return pv.isReturnPVToInitiator === true || pv.returnPV2InitiatorInd === 1
}

const currentUserId = computed(() => userStore.profile?.id || userStore.profile?.uuid || null)

const hasSavedDOPRemark = computed(() => {
  const pv = pvPayload.value || {}
  return Boolean(pv.dirOfPaymentRemarks) && isFirstLevelApprovedByBackend(pv)
})
const hasSavedDCADRemark = computed(() => {
  const pv = pvPayload.value || {}
  return Boolean(pv.dirCadRemarks) && isFirstLevelApprovedByBackend(pv)
})
const hasSavedDOTRemark = computed(() => {
  const pv = pvPayload.value || {}
  return Boolean(pv.dirOfTreasuryRemarks) && isSecondLevelApprovedByBackend(pv)
})
const hasSavedAGRemark = computed(() => {
  const pv = pvPayload.value || {}
  return Boolean(pv.acctGeneralRemarks) && isSecondLevelApprovedByBackend(pv)
})

const isPVCompleted = computed(() => {
  const pv = pvPayload.value || {}
  return pv.completedPVInd === 1 || pv.completed === true
})

const isPVReturned = computed(() => {
  const pv = pvPayload.value || {}
  return pv.returnedPVInd === 1 || pv.returned === true
})

const isReturnPVToInitiator = computed(() => {
  return isReturnPVToInitiatorByBackend(pvPayload.value || {})
})

const lastApprovedLevel = computed(() => {
  const pv = pvPayload.value || {}
  const firstApproved = isFirstLevelApprovedByBackend(pv)
  const secondApproved = isSecondLevelApprovedByBackend(pv)

  if (secondApproved && !firstApproved) return 'second'
  if (firstApproved && !secondApproved) return 'first'
  return null
})

const userBelongsToFirstLevel = computed(() => {
  return hasRole('dirOfPayment') || hasRole('dirCAD')
})

const userBelongsToSecondLevel = computed(() => {
  return hasRole('dirOfTreasury') || hasRole('acctGeneral')
})

const canActOnReturnedPV = computed(() => {
  if (isReturnPVToInitiator.value) return false
  if (!isPVReturned.value) return true

  if (lastApprovedLevel.value === 'first') return userBelongsToFirstLevel.value
  if (lastApprovedLevel.value === 'second') return userBelongsToSecondLevel.value
  return false
})

const pvStatusLabel = computed(() => {
  const pv = pvPayload.value || {}
  if (pv.pvStatus) return String(pv.pvStatus)
  if (pv.completedPVInd === 1 || pv.completed) return 'Completed'
  if (pv.returnedPVInd === 1 || pv.returned) return 'Returned'
  if (pv.pendingPVInd === 1 || pv.pending) return 'Pending'
  return 'Active'
})

const firstLevelSaved = computed(() => {
  const pv = pvPayload.value || {}
  return isFirstLevelApprovedByBackend(pv) || hasSavedDOPRemark.value || hasSavedDCADRemark.value
})

const secondLevelSaved = computed(() => {
  const pv = pvPayload.value || {}
  return isSecondLevelApprovedByBackend(pv) || hasSavedDOTRemark.value || hasSavedAGRemark.value
})

// const firstApproved = computed(() => {
//   const pv = pvPayload.value || {}
//   return Boolean(pv.is1stLvlApproved || firstLevelSaved.value)
// })

const firstLevelApproverKey = computed(() => {
  const pv = pvPayload.value || {}

  if (pv.dirCadRemarks) return 'dcad'
  if (pv.dirOfPaymentRemarks) return 'dop'

  if (isPVReturned.value) {
    if (hasRole('dirCAD')) return 'dcad'
    if (hasRole('dirOfPayment')) return 'dop'
  }

  if (hasSavedDOPRemark.value) return 'dop'
  if (hasSavedDCADRemark.value) return 'dcad'
  if (hasRole('dirOfPayment')) return 'dop'
  if (hasRole('dirCAD')) return 'dcad'
  return null
})

const secondLevelApproverKey = computed(() => {
  const pv = pvPayload.value || {}

  if (pv.dirOfTreasuryRemarks) return 'dot'
  if (pv.acctGeneralRemarks) return 'ag'

  if (isPVReturned.value) {
    if (hasRole('acctGeneral')) return 'ag'
    if (hasRole('dirOfTreasury')) return 'dot'
  }

  if (hasSavedDOTRemark.value) return 'dot'
  if (hasSavedAGRemark.value) return 'ag'
  if (hasRole('dirOfTreasury')) return 'dot'
  if (hasRole('acctGeneral')) return 'ag'
  return null
})

const firstLevelLabel = computed(() => firstLevelApproverKey.value === 'dcad' ? 'DCAD Remark' : 'Director of Payment Remark')
const secondLevelLabel = computed(() => secondLevelApproverKey.value === 'ag' ? 'Accountant General Remark' : 'Director of Treasury Remark')

const firstLevelBoxVisible = computed(() => Boolean(firstLevelApproverKey.value))
const secondLevelBoxVisible = computed(() => {
  if (!secondLevelApproverKey.value) return false
  if (secondLevelSaved.value) return true
  return true
})

const canEditFirstLevelRemark = computed(() => {
  if (!canActOnReturnedPV.value) return false
  const key = firstLevelApproverKey.value

  if (isPVReturned.value && !isReturnPVToInitiator.value) {
    if (key === 'dop') return hasRole('dirOfPayment')
    if (key === 'dcad') return hasRole('dirCAD')
    return false
  }

  if (key === 'dop') return hasRole('dirOfPayment') && !firstLevelSaved.value
  if (key === 'dcad') return hasRole('dirCAD') && !firstLevelSaved.value
  return false
})

const canEditSecondLevelRemark = computed(() => {
  if (!canActOnReturnedPV.value) return false

  if (isPVReturned.value && !isReturnPVToInitiator.value) {
    const key = secondLevelApproverKey.value
    if (key === 'dot') return hasRole('dirOfTreasury')
    if (key === 'ag') return hasRole('acctGeneral')
    return false
  }

  if (secondLevelSaved.value) return false
  const key = secondLevelApproverKey.value
  if (key === 'dot') return hasRole('dirOfTreasury')
  if (key === 'ag') return hasRole('acctGeneral')
  return false
})

// const isSecondLevelViewer = computed(() => {
//   return hasRole('dirOfTreasury') || hasRole('acctGeneral')
// })<|diff_marker|>

// const secondLevelReturnContext = computed(() => {
//   const pv = pvPayload.value || {}

//   if (pv.dirCadRemarks) {
//     return { label: 'Returned by DCAD', value: pv.dirCadRemarks }
//   }

//   if (pv.dirOfPaymentRemarks) {
//     return { label: 'Returned by Director of Payment', value: pv.dirOfPaymentRemarks }
//   }

//   if (pv.dirOfTreasuryRemarks) {
//     return { label: 'Returned by Director of Treasury', value: pv.dirOfTreasuryRemarks }
//   }

//   if (pv.acctGeneralRemarks) {
//     return { label: 'Returned by Accountant General', value: pv.acctGeneralRemarks }
//   }

//   return null
// })

// const showSecondLevelReturnContext = computed(() => {
//   return isPVReturned.value && Boolean(secondLevelReturnContext.value) && isSecondLevelViewer.value && secondLevelBoxVisible.value
// })

const canShowApproveButton = computed(() => {
  if (isReturnedPV.value) return false
  if (!canActOnReturnedPV.value) return false
  const canEdit = canEditFirstLevelRemark.value || canEditSecondLevelRemark.value
  if (!canEdit) return false

  return !isPVCompleted.value
})

const canShowReturnButton = computed(() => {
  if (!canActOnReturnedPV.value) return false
  const canEdit = canEditFirstLevelRemark.value || canEditSecondLevelRemark.value
  if (!canEdit) return false

  return !isPVCompleted.value
})

const getRequiredRemarkValue = () => {
  if (canEditFirstLevelRemark.value) return String(form.dopRemarks || '').trim()
  if (canEditSecondLevelRemark.value) return String(form.dotRemarks || '').trim()
  return ''
}

const validateRequiredRemark = async (actionLabel) => {
  const remark = getRequiredRemarkValue()
  if (remark) return true

  await Swal.fire({
    icon: 'warning',
    title: 'Remark is required',
    text: `You cannot ${actionLabel.toLowerCase()} without filling the remark field.`
  })

  return false
}

const getFirstLevelRemarkForView = (data) => {
  if (firstLevelApproverKey.value === 'dcad') return data?.dirCadRemarks || ''
  if (firstLevelApproverKey.value === 'dop') return data?.dirOfPaymentRemarks || ''
  return data?.dirCadRemarks || data?.dirOfPaymentRemarks || ''
}

const getSecondLevelRemarkForView = (data) => {
  if (secondLevelApproverKey.value === 'ag') return data?.acctGeneralRemarks || ''
  if (secondLevelApproverKey.value === 'dot') return data?.dirOfTreasuryRemarks || ''
  return data?.acctGeneralRemarks || data?.dirOfTreasuryRemarks || ''
}

const fetchWarrantEconomicItems = async (warrantId) => {
  if (!warrantId) {
    return false
  }

  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/warrant/${warrantId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    economicItems.value = (data?.warrantEconomicLineItems || []).map(dto => ({
      id: dto.id,
      ecoCode: dto.ecoCode,
      title: dto.title,
      mdaLineItemBudgetId: dto.mdaLineItemBudgetId,
      amountBudgeted: dto.amountBudgeted,
      amountReleased: dto.amountReleased,
      balance: dto.balance,
      amountRequired: dto.amountRequired,
      year: dto.year,
      quarter: dto.quarter,
      eliAmountRecommended: dto.amountRecommended || 0
    }))

    return true
  } catch (err) {
    console.error('Error loading warrant economic items:', err)
    return false
  }
}

const normalizeForForm = (data) => {
  form.beneficiary = data?.payee || ''
  form.pvInitDate = data?.pvInitDate || ''
  form.paymentDate = data?.paymentDate || ''
  form.bank = data?.bankName || ''
  form.grossAmount = Number(data?.grossAmount || 0).toLocaleString()
  form.deductions = Number((data?.deductions || []).reduce((sum, item) => sum + Number(item?.amount || 0), 0)).toLocaleString()
  form.netAmount = Number(data?.netAmount || 0).toLocaleString()
  form.dopRemarks = getFirstLevelRemarkForView(data)
  form.dotRemarks = getSecondLevelRemarkForView(data)

  deductions.value = (data?.deductions || []).map(item => ({
    deduction: item?.dedTypeName || 'N/A',
    description: item?.dedTypeName || 'N/A',
    percentage: item?.percentage || 0,
    amountReleased: item?.amount || 0,
  }))

}

const fetchPaymentVoucher = async () => {
  loadingDetails.value = true
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/payment/voucher/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    pvPayload.value = {
      ...data,
      natureOfExpenditure: data?.natureOfExpenditure || '',
    }
    normalizeForForm(data)

    try {
      const loadedFromWarrant = await fetchWarrantEconomicItems(data?.warrantId)
      if (!loadedFromWarrant) {
        economicItems.value = data?.economicItems || data?.paymentVoucherEconomicItems || []
      }
    } catch (warrantError) {
      console.error('Error loading warrant economic items:', warrantError)
      economicItems.value = data?.economicItems || data?.paymentVoucherEconomicItems || []
    }

  } catch (err) {
    console.error('Error loading payment voucher:', err)
    const errorMessage = err?.response?.data?.errorMessage || err?.response?.data?.message || err?.response?.data?.error || 'Failed to load payment voucher details.'
    await Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${errorMessage}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loadingDetails.value = false
  }
}

const makeUpdatedPayload = (overrides = {}) => ({
  ...(pvPayload.value || {}),
  id: (pvPayload.value || {}).id || id,
  natureOfExpenditure: (pvPayload.value || {}).natureOfExpenditure || '',
  ...overrides,
})

const savePaymentVoucher = async (payload) => {
  const token = Cookies.get('auth_token')
  const { data } = await axios.put(`${root_api_url}/payment/voucher/${id}`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  })
  pvPayload.value = data
  normalizeForForm(data)
}

const handleApprove = async () => {
  const nowIso = new Date().toISOString()
  const userId = currentUserId.value

  if (!pvPayload.value) return

  if (!userId) {
    await Swal.fire({ icon: 'error', title: 'Error', text: 'User profile not loaded. Please refresh and try again.' })
    return
  }

  const hasRemark = await validateRequiredRemark('Approve')
  if (!hasRemark) return

  loadingAccept.value = true
  submitted.value = true

  try {
    if (canEditFirstLevelRemark.value) {
      const isFinalApproval = isSecondLevelApprovedByBackend(pvPayload.value || {})

      const payload = hasRole('dirOfPayment')
        ? makeUpdatedPayload({
          dirOfPaymentId: userId,
          dirOfPaymentRemarks: form.dopRemarks.trim(),
          dirOfPaymentRemarksTs: nowIso,
          is1stLvlApproved: true,
          pendingPVInd: isFinalApproval ? 0 : 1,
          pending: !isFinalApproval,
          completedPVInd: isFinalApproval ? 1 : 0,
          completed: isFinalApproval,
          returnedPVInd: 0,
          returned: false,
        })
        : makeUpdatedPayload({
          dirCadId: userId,
          dirCadRemarks: form.dopRemarks.trim(),
          dirCadRemarksTs: nowIso,
          is1stLvlApproved: true,
          pendingPVInd: isFinalApproval ? 0 : 1,
          pending: !isFinalApproval,
          completedPVInd: isFinalApproval ? 1 : 0,
          completed: isFinalApproval,
          returnedPVInd: 0,
          returned: false,
        })

      await savePaymentVoucher(payload)
      await Swal.fire({ icon: 'success', title: 'Success', text: 'First level approval completed successfully.' })
      return
    }

    if (canEditSecondLevelRemark.value) {
      const isFinalApproval = isFirstLevelApprovedByBackend(pvPayload.value || {})

      const payload = hasRole('dirOfTreasury')
        ? makeUpdatedPayload({
          dirOfTreasuryId: userId,
          dirOfTreasuryRemarks: form.dotRemarks.trim(),
          dirOfTreasuryRemarksTs: nowIso,
          is2ndLvlApproved: true,
          completedPVInd: isFinalApproval ? 1 : 0,
          completed: isFinalApproval,
          pendingPVInd: isFinalApproval ? 0 : 1,
          pending: !isFinalApproval,
          returnedPVInd: 0,
          returned: false,
        })
        : makeUpdatedPayload({
          acctGeneralId: userId,
          acctGeneralRemarks: form.dotRemarks.trim(),
          acctGeneralRemarksTs: nowIso,
          is2ndLvlApproved: true,
          completedPVInd: isFinalApproval ? 1 : 0,
          completed: isFinalApproval,
          pendingPVInd: isFinalApproval ? 0 : 1,
          pending: !isFinalApproval,
          returnedPVInd: 0,
          returned: false,
        })

      await savePaymentVoucher(payload)
      await Swal.fire({ icon: 'success', title: 'Success', text: 'Second level approval completed successfully.' })
      return
    }

    await Swal.fire({
      icon: 'info',
      title: 'No action',
      text: 'No pending approval action is available for your role at this stage.'
    })
  } catch (error) {
    const errorMessage = error?.response?.data?.errorMessage || error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unable to complete approval.'
    await Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${errorMessage}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loadingAccept.value = false
    submitted.value = false
  }
}

const handleReturn = async () => {
  if (!pvPayload.value) return

  const hasRemark = await validateRequiredRemark('Return')
  if (!hasRemark) return

  const shouldContinue = await returnConfirmation()
  if (!shouldContinue) return

  const nowIso = new Date().toISOString()
  const userId = currentUserId.value

  if (!userId) {
    await Swal.fire({ icon: 'error', title: 'Error', text: 'User profile not loaded. Please refresh and try again.' })
    return
  }

  loadingReturn.value = true
  submitted.value = true

  try {
    if (canEditFirstLevelRemark.value) {
      const payload = hasRole('dirOfPayment')
        ? makeUpdatedPayload({
          dirOfPaymentId: userId,
          dirOfPaymentRemarks: form.dopRemarks.trim(),
          dirOfPaymentRemarksTs: nowIso,
          returnedPVInd: 1,
          returned: true,
          pendingPVInd: 0,
          pending: false,
          completedPVInd: 0,
          completed: false,
          is1stLvlApproved: false,
          is2ndLvlApproved: false,
          dirOfTreasuryId: null,
          dirOfTreasuryRemarks: '',
          dirOfTreasuryRemarksTs: null,
          acctGeneralId: null,
          acctGeneralRemarks: '',
          acctGeneralRemarksTs: null,
        })
        : makeUpdatedPayload({
          dirCadId: userId,
          dirCadRemarks: form.dopRemarks.trim(),
          dirCadRemarksTs: nowIso,
          returnedPVInd: 1,
          returned: true,
          pendingPVInd: 0,
          pending: false,
          completedPVInd: 0,
          completed: false,
          is1stLvlApproved: false,
          is2ndLvlApproved: false,
          dirOfTreasuryId: null,
          dirOfTreasuryRemarks: '',
          dirOfTreasuryRemarksTs: null,
          acctGeneralId: null,
          acctGeneralRemarks: '',
          acctGeneralRemarksTs: null,
        })

      await savePaymentVoucher(payload)
      await Swal.fire({ icon: 'success', title: 'Success', text: 'Payment voucher returned successfully for correction.' })
      return
    }

    if (canEditSecondLevelRemark.value) {
      const payload = hasRole('dirOfTreasury')
        ? makeUpdatedPayload({
          dirOfTreasuryId: userId,
          dirOfTreasuryRemarks: form.dotRemarks.trim(),
          dirOfTreasuryRemarksTs: nowIso,
          returnedPVInd: 1,
          returned: true,
          pendingPVInd: 0,
          pending: false,
          completedPVInd: 0,
          completed: false,
          is1stLvlApproved: false,
          is2ndLvlApproved: false,
        })
        : makeUpdatedPayload({
          acctGeneralId: userId,
          acctGeneralRemarks: form.dotRemarks.trim(),
          acctGeneralRemarksTs: nowIso,
          returnedPVInd: 1,
          returned: true,
          pendingPVInd: 0,
          pending: false,
          completedPVInd: 0,
          completed: false,
          is1stLvlApproved: false,
          is2ndLvlApproved: false,
        })

      await savePaymentVoucher(payload)
      await Swal.fire({ icon: 'success', title: 'Success', text: 'Payment voucher returned successfully.' })
      return
    }

    await Swal.fire({
      icon: 'info',
      title: 'No action',
      text: 'No pending return action is available for your role at this stage.'
    })
  } catch (error) {
    const errorMessage = error?.response?.data?.errorMessage || error?.response?.data?.message || error?.response?.data?.error || 'Unable to return payment voucher.'
    await Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${errorMessage}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loadingReturn.value = false
    submitted.value = false
  }
}

const returnConfirmation = async () => {
  const pv = pvPayload.value || {}
  const isReturned = pv.returned === true || pv.returnedPVInd === 1

  if (!isReturned) {
    return true
  }

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Return payment voucher?',
    text: 'Are you sure you want to return this payment voucher?',
    showCancelButton: true,
    confirmButtonText: 'Yes, return voucher',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
  })

  return Boolean(result.isConfirmed)
}

const isReturnedPV = computed(() => {
  const pv = pvPayload.value || {}
  return pv.returnedPVInd === 1 || pv.returned === true
})

const formatInitDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'long' })
  const year = date.getFullYear()

  const getOrdinal = (number) => {
    const remainder = number % 100
    if (remainder >= 11 && remainder <= 13) return `${number}th`
    switch (number % 10) {
      case 1:
        return `${number}st`
      case 2:
        return `${number}nd`
      case 3:
        return `${number}rd`
      default:
        return `${number}th`
    }
  }

  return `${getOrdinal(day)} ${month} ${year}`
}

onMounted(async () => {
  if (!id) {
    await Swal.fire({
      icon: 'error',
      title: 'Missing voucher id',
      text: 'Open this page with a valid payment voucher id.'
    })
    return
  }

  if (!userStore.profile) {
    await userStore.fetchProfile()
  }

  await fetchPaymentVoucher()
  document.title = "PV Approval"
})


</script>

<style scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
  border-radius: 4px;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
  word-break: break-word;
  overflow-wrap: break-word;
}

.payment-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  /* forces every .col to match the tallest */
}

.payment-item-wrapper .view-only-input,
.payment-item-wrapper input.view-only-input {
  flex: 1;
  /* inputs (and your Approval‐Authority <div>) all grow equally */
  /* for your Approval Authority div: */
  white-space: pre-wrap;
}

.dashboard-layout .payment-item-wrapper {
  margin-top: 20px;
  padding: 0;
}

.dashboard-layout .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.dashboard-layout .section-border {
  /* padding: 1rem; */
  border-top: 3px solid #667eea !important;
}


.dashboard-layout .payment-item-wrapper .col {
  /* padding: 0; */
  border-right: 1px solid var(--border);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
}

.dashboard-layout .payment-item-wrapper .col.no-border {
  border-right: none !important;
}

.dashboard-layout .payment-item-wrapper .fixed-label {
  min-height: 80px;
  justify-content: center;
  width: auto;
  box-sizing: border-box;
  text-align: center;
  /* all: unset; */
  display: flex;
  margin-bottom: 0;
  flex-shrink: 0;
  /* margin-left: 1rem; */
}


.dashboard-layout .payment-item-wrapper .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* padding: 1rem; */
}

.dashboard-layout .payment-item-wrapper .content {
  border-bottom: 2px solid var(--border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.dashboard-layout .payment-item-wrapper label {
  font-size: 16px;
  padding: .375rem 0 !important;
  margin-bottom: 0px !important;
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.dashboard-layout .payment-item-wrapper .col-md-3.no-border {
  border: none;
}

.dashboard-layout .payment-item-wrapper .col-md-3.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .payment-item-wrapper .col-md-2.no-border {
  border: none;
}

.dashboard-layout .payment-item-wrapper .col-md-2.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .fixed-label {
  display: flex;
  align-items: center;
  /* vertical center */
  justify-content: center;
  /* horizontal center */
  min-height: 4rem;
  width: 100%;
  padding: 0 0.25rem;
  box-sizing: border-box;
  text-align: center;
}

/* Submit Button */
.btn-submit {
  background-color: #10b981;
  border-color: #10b981;
  color: #ffffff;
  padding: 0.625rem 3rem;
  font-weight: 500;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-submit:hover {
  background-color: #059669;
  border-color: #059669;
  color: #ffffff;
}

.btn-submit:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}
</style>
